import { RootState } from "@/redux/store";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { statusOptions, validateCards } from "../../consts";
import { capitalize, cn } from "@/lib/utils";
import { Badge } from "@/components/ui";
import { useCommitBulkUploadPropertyMutation } from "@/services";
import { toast } from "@/components/Toast";
import { setBulkUploadDtl } from "@/redux/slices";

const useStep2 = ({onNextStepClick}: {onNextStepClick?: () => void}) => {
  const dispatch = useDispatch();
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const bulkUploadDtl = useSelector(
    (state: RootState) => state.currentData.bulkUploadDtl
  );
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const summaryCards = useMemo(() => {
    return validateCards?.map((data: any) => ({...data, count: (bulkUploadDtl[data?.key])?.toString()?.padStart(2, "0")}))
  }, [bulkUploadDtl]);

  const columns = [
    {
      accessorKey: "row",
      header: <div>Row</div>,
      accessorFn: (row: any) => String(row.row || ""),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const currStatus = statusOptions?.find((data: any) => data?.value?.toLowerCase() == row?.original?.status);
        const Icon: any = currStatus?.icon;
        return <Badge className={cn(currStatus?.className, currStatus?.textClassName, "rounded-full")}>
          <Icon /> {capitalize(row?.original?.status || "")}
        </Badge>
      }
    },
    {
      accessorKey: "propertyName",
      header: <div>Property</div>,
      accessorFn: (row: any) => String(row.propertyName || ""),
    },
    {
      accessorKey: "issues",
      header: <div>Issues Found</div>,
      cell: ({row}: any) => {
        return <ul className="list-disc pl-4">
          {row?.original?.issues?.map((data: any) => <li>{data}</li>)}
        </ul>
      },
    },
  ];

  const { mutate: commitUpload, isPending: isUploadPending } =
    useCommitBulkUploadPropertyMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        dispatch(setBulkUploadDtl({...bulkUploadDtl, commitData: {...data?.data?.data} }))
        onNextStepClick?.();
      },
    });

  const onFilterChange = (filter: string) => {
    setActiveFilter(filter)
  }

  const tableData = useMemo(() => {
    if (activeFilter == "all") return bulkUploadDtl?.rows;
    return bulkUploadDtl?.rows?.filter((data: any) => data?.status?.toLowerCase() == activeFilter)
  }, [bulkUploadDtl, activeFilter]);

  const onImport = () => {
    commitUpload({
      communityId: communityData?.communityId,
      attachmentId: bulkUploadDtl?.attachmentId,
      data: {
        "includeWarnings": true
      }
    })
  }

  const enableButton = useMemo(() => (bulkUploadDtl?.readyToImportCount || bulkUploadDtl?.warningCount) && !bulkUploadDtl?.errorCount, [bulkUploadDtl]);

  const importCount = useMemo(() => bulkUploadDtl?.errorCount ? "0" : bulkUploadDtl?.readyToImportCount || bulkUploadDtl?.warningCount || "0", [bulkUploadDtl]);

  return {
    communityName: communityData?.communityName || communityData?.name,
    bulkUploadDtl,
    summaryCards,
    columns,
    onFilterChange,
    tableData,
    activeFilter,
    isUploadPending,
    onImport,
    enableButton,
    importCount
  }
}

export default useStep2;